/**
 * Orthomosaic / survey WMTS checkbox linking
 *
 * Slug suffix distinguishes scope:
 * - Survey year: network-wide shared tileset (link checkboxes)
 * - Survey date + run index: survey-specific (independent)
 */

export type SurveyOrthoScope = 'network' | 'survey';

/** Individual survey: survey date + run index */
const SURVEY_DATE_RUN_SLUG_SUFFIX = /(?:^|-)\d{4}-\d{2}-\d{2}-\d+$/;

export interface SurveyWmtsLayerLike {
  readonly key: string;
  readonly name: string;
  readonly type: string;
  readonly endpoint: string;
  readonly checked?: boolean;
  readonly sourceSlug?: string;
}

export function isSurveyWmtsLayer(layer: {
  readonly type?: string;
  readonly endpoint?: string;
}): boolean {
  const urlLower = (layer.endpoint || '').toLowerCase();
  return (
    (layer.type || '').toUpperCase() === 'WMTS' ||
    urlLower.includes('service=wmts') ||
    urlLower.includes('wmts')
  );
}

/**
 * Resolve whether a WMTS ortho slug is network-wide (survey year) or
 * survey-specific (date + run)
 */
export function resolveSurveyOrthoScopeFromSlug(slug: string): SurveyOrthoScope {
  const normalized = (slug || '').trim().toLowerCase();
  if (!normalized) {
    return 'network';
  }
  if (SURVEY_DATE_RUN_SLUG_SUFFIX.test(normalized)) {
    return 'survey';
  }
  return 'network';
}

export function isNetworkWideOrthoSlug(slug: string): boolean {
  return resolveSurveyOrthoScopeFromSlug(slug) === 'network';
}

export function isSurveySpecificOrthoSlug(slug: string): boolean {
  return resolveSurveyOrthoScopeFromSlug(slug) === 'survey';
}

export function resolveLayerOrthoScope(layer: {
  readonly sourceSlug?: string;
}): SurveyOrthoScope {
  return resolveSurveyOrthoScopeFromSlug(layer.sourceSlug ?? '');
}

/**
 * Network-wide orthos share a type/year/name key
 * Survey-specific orthos are keyed by survey + layer so checkboxes stay independent
 */
export function buildSurveyWmtsLayerKey(params: {
  readonly sourceSlug: string;
  readonly layerName: string;
  readonly surveySlug: string;
  readonly surveyType: string;
  readonly surveyYear: string;
}): string {
  if (isSurveySpecificOrthoSlug(params.sourceSlug)) {
    return `${params.surveySlug}_${params.layerName}`;
  }
  return `${params.surveyType}_${params.surveyYear}_${params.layerName}`;
}

/**
 * Cross-link only network-wide WMTS layers that share the same display name
 * Survey-specific orthos never link.
 */
export function shouldLinkWmtsLayers(
  source: SurveyWmtsLayerLike,
  target: SurveyWmtsLayerLike,
): boolean {
  if (!isSurveyWmtsLayer(source) || !isSurveyWmtsLayer(target)) {
    return false;
  }
  if (resolveLayerOrthoScope(source) !== 'network') {
    return false;
  }
  if (resolveLayerOrthoScope(target) !== 'network') {
    return false;
  }
  return source.name === target.name;
}
